import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { cn } from '../../lib/utils';
import { MarkdownView } from './MarkdownView';
import { ImageView } from './ImageView';
import { TableView } from './TableView';
import { PdfView } from './PdfView';
import { UnsupportedView } from './UnsupportedView';
import { Button } from '../button';
import type { ViewerSourceEditing } from './TextEditorView';
import { LoadingState } from '../shogun-spinner';

// The CM6 editor (and the language registry behind it) is the heavy chunk —
// loaded only when a text/code/html file is actually opened.
const TextEditorView = React.lazy(() =>
  import('./TextEditorView').then((m) => ({ default: m.TextEditorView })),
);
const HtmlView = React.lazy(() => import('./HtmlView').then((m) => ({ default: m.HtmlView })));
// The conflict merge surface (and `@codemirror/merge` behind it) loads only
// when a conflict is actually reviewed.
const MergeView = React.lazy(() => import('./MergeView').then((m) => ({ default: m.MergeView })));

/**
 * The content of one file, as returned by the read path. `encoding` decides how
 * `content` is interpreted: `'utf8'` → text verbatim; `'base64'` → binary bytes.
 * Kept SDK-free so `@localagent/ui` stays a pure component library; the app-shell
 * adapter maps the SDK's `FileReadResult` (structurally identical) onto this.
 */
export interface FileContent {
  content: string;
  encoding: 'utf8' | 'base64';
}

// --- editing surface ---------------------------------------------------------

/**
 * Live text is CM6-owned; the host syncs it at this idle boundary (the
 * autosave debounce — the sync IS the idle detector the host's
 * autosave keys off). Module-local (not exported): a runtime-value export
 * from a component .tsx disables Vite Fast Refresh for the module.
 */
const BUFFER_SYNC_DEBOUNCE_MS = 1500;

/** The host-detected "file changed under this tab" state. */
export interface FileEditingExternalChange {
  /** Null ⇒ the file no longer exists (delete-conflict). */
  currentVersion: string | null;
  /** Present only when the CAS conflict carried the live content. */
  currentContent?: string;
}

/** Host-owned per-open-file editing state the pane renders chrome from. */
export interface FileEditingState {
  dirty: boolean;
  saving: boolean;
  /** Non-conflict save failure message (banner + retry). */
  saveError: string | null;
  externalChange: FileEditingExternalChange | null;
  /** Path vanished in a refresh (delete-conflict treatment). */
  notFound: boolean;
  /** Bumped by the host when it replaces the buffer (reload / silent reload)
   *  so the pane re-resolves content through `read`. */
  epoch: number;
}

export type FileEditingResolveAction =
  | 'reload' // take the incoming content (only offered when it was carried)
  | 'keep-mine' // immediate CAS save with expected_version = currentVersion (ui ADR-003)
  | 'merged' // merge-view output becomes the DIRTY buffer; carries the text
  | 'save-as-new' // unconditional write recreating a deleted file (explicit user act)
  | 'retry-save' // re-run the failed save (non-conflict failure banner)
  | 'save-close' // save (as-new when notFound), close the tab on success
  | 'discard-close'; // drop the buffer and close the tab

/**
 * Optional controlled-editing wiring. When absent the pane
 * behaves exactly as before: read-only, self-fetching. When present, the live
 * text stays inside CM6 and is pushed to the host ONLY at sync points
 * (debounce boundary, Cmd/Ctrl+S, tab switch, close) — never per keystroke.
 */
export interface FileViewerEditingProps {
  /** True renders the editor read-only with `readOnlyNotice`. */
  readOnly: boolean;
  readOnlyNotice?: string;
  /** Editing state for the CURRENT `path`. */
  state: FileEditingState;
  /** Fired immediately on every doc change (host flips dirty once; no text). */
  onEdit: (path: string) => void;
  /** Buffer text at a sync point — the host's autosave trigger. */
  onBufferSync: (path: string, text: string) => void;
  /** Manual save (Cmd/Ctrl+S — the pane flushes the pending sync first). */
  onSave: (path: string) => void;
  /** `mergedText` accompanies ONLY the `merged` action (the merge view's
   *  edited output — it becomes the host's dirty buffer). */
  onResolve: (path: string, action: FileEditingResolveAction, mergedText?: string) => void;
  editMode?: boolean;
  onEditModeChange?: (editMode: boolean) => void;
}

export interface FileViewerPaneProps {
  /** Workspace-relative path of the selected file, or null when nothing is selected. */
  path: string | null;
  /** Reads a file's content + encoding. Provided by the host (real backend read). */
  read: (path: string) => Promise<FileContent>;
  /**
   * Open the file outside the app (open-in-OS on desktop, download on web).
   * Receives the decoded bytes so a web host can build a download blob without
   * re-reading. Wired for unsupported formats. May be async (the desktop
   * OS-open path is an IPC round-trip) — rejections surface on the card.
   */
  onOpenExternally?: (path: string, bytes: Uint8Array) => void | Promise<void>;
  /**
   * Byte size of `path`, from the tree listing. When provided, a file larger than
   * the inline limit is NOT fetched — the pane shows a placeholder instead of
   * freezing the renderer parsing a huge payload. Omit if unknown.
   */
  sizeBytes?: number;
  /** Controlled-editing wiring; absent ⇒ today's read-only behavior. */
  editing?: FileViewerEditingProps;
  /**
   * Internal host wiring for chrome rendered OUTSIDE the pane (the tab
   * strip's dirty-close dialog): invoking it flushes any pending buffer sync
   * for the current path so a host-side save acts on the latest text,
   * including sub-debounce edits. Set while the pane is mounted.
   */
  flushPendingEditsRef?: MutableRefObject<(() => void) | null>;
  className?: string;
}

// --- extension → renderer category ---------------------------------------

const CODE_EXTS = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs', 'py', 'go', 'rs', 'json', 'css',
  'scss', 'xml', 'sh', 'bash', 'yml', 'yaml', 'toml', 'sql', 'java',
  'c', 'h', 'cpp', 'hpp', 'rb', 'php', 'swift', 'kt', 'txt', 'log', 'env',
]);
// HTML gets the sandboxed rendered preview (with a View/Edit switch), not the
// source-only code treatment.
const HTML_EXTS = new Set(['html', 'htm']);
const EDIT_TOGGLE_EXTS = new Set(['md', 'markdown', ...HTML_EXTS]);
const IMAGE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  svg: 'image/svg+xml',
};
function extOf(path: string): string {
  const base = path.split('/').pop() ?? path;
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
}

export function hasEditToggle(path: string): boolean {
  return EDIT_TOGGLE_EXTS.has(extOf(path));
}

// Fetch+render inline only up to this size; a workspace file is read WHOLE (the
// backend returns its full content as a base64/utf8 string), so pulling a
// hundred-MB file back and parsing it on the renderer's main thread freezes the
// app. We refuse before the read, using the size the tree already reported.
const MAX_INLINE_BYTES = 2_000_000;
// A user-initiated download may go larger, but still bounded so a deliberate
// click can't wedge the renderer either.
const MAX_DOWNLOAD_BYTES = 25_000_000;

// The extensions this viewer can actually render. Anything else is NOT fetched —
// there's no point base64-ing a `.zip` just to show an "unsupported" card.
// Office formats (.xlsx/.xls/.ods/.docx) are deliberately absent: they route to
// the OS-open path, which fetches on demand.
const RENDERABLE_EXTS = new Set<string>([
  ...CODE_EXTS,
  ...HTML_EXTS,
  'md', 'markdown', 'csv', 'tsv', 'pdf',
  ...Object.keys(IMAGE_MIME),
]);
// Common extensionless text files we still preview as code.
const TEXT_BASENAMES = new Set([
  'Dockerfile', 'Makefile', 'LICENSE', 'README', '.gitignore', '.dockerignore',
  '.env', '.npmrc', '.gitattributes', '.editorconfig',
]);

function canPreview(path: string): boolean {
  const base = path.split('/').pop() ?? path;
  return TEXT_BASENAMES.has(base) || RENDERABLE_EXTS.has(extOf(path));
}

function formatBytes(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} KB`;
  return `${n} B`;
}

function bytesFromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Decode a FileContent to raw bytes regardless of transport encoding. */
function bytesOf(fc: FileContent): Uint8Array {
  return fc.encoding === 'base64'
    ? bytesFromBase64(fc.content)
    : new TextEncoder().encode(fc.content);
}

/** Decode a FileContent to text regardless of transport encoding. */
function textOf(fc: FileContent): string {
  return fc.encoding === 'base64'
    ? new TextDecoder().decode(bytesFromBase64(fc.content))
    : fc.content;
}

/**
 * Rich multi-format file viewer. Dispatches by file extension (and the read
 * `encoding`) to the appropriate renderer, decoding base64 → Blob for the
 * image/pdf paths. Unknown formats fall to an Open/Download action — never raw
 * bytes.
 *
 * Read/decoding is done here; the leaf renderers stay pure. Loading and error
 * are explicit states (no silent blank).
 *
 * With `editing` the pane owns the EDITING CHROME —
 * external-change/conflict banners, delete-conflict card, save-error banner,
 * read-only notice — and threads the buffer wiring into the text-capable
 * viewers. Images/PDF/CSV/unsupported ignore editing entirely.
 */
export function FileViewerPane({ path, read, onOpenExternally, sizeBytes, editing, flushPendingEditsRef, className }: FileViewerPaneProps) {
  const [fc, setFc] = useState<FileContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // --- editing sync wiring (sync points) -----------------------------------
  // The live text lives in CM6; `getTextRef` pulls it on demand. Host state is
  // touched only at sync points: the idle debounce below, Cmd/Ctrl+S, tab
  // switch and close (the [path] cleanup). Per keystroke we do exactly two
  // cheap things: notify the host (`onEdit`, which flips dirty once) and
  // restart the debounce timer. `syncPending` re-renders once per burst
  // (setState(true) while already true bails out).
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const pathRef = useRef(path);
  pathRef.current = path;
  const getTextRef = useRef<(() => string) | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [syncPending, setSyncPending] = useState(false);
  // Buffer text as of the last sync point — feeds the markdown/html PREVIEW
  // sides (they must render the buffer, not disk) without re-rendering
  // per keystroke.
  const [syncedText, setSyncedText] = useState<string | null>(null);

  const flushBufferSync = useCallback((p: string) => {
    if (syncTimerRef.current != null) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    setSyncPending(false);
    const text = getTextRef.current?.();
    if (text == null) return;
    setSyncedText(text);
    editingRef.current?.onBufferSync(p, text);
  }, []);

  const handleDocChanged = useCallback(() => {
    const p = pathRef.current;
    if (!p) return;
    editingRef.current?.onEdit(p);
    setSyncPending(true);
    if (syncTimerRef.current != null) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      flushBufferSync(p);
    }, BUFFER_SYNC_DEBOUNCE_MS);
  }, [flushBufferSync]);

  const handleEditorSave = useCallback(() => {
    const p = pathRef.current;
    if (!p) return;
    // Cmd/Ctrl+S flushes the pending sync (cancelling the autosave debounce)
    // and then saves through the host.
    flushBufferSync(p);
    editingRef.current?.onSave(p);
  }, [flushBufferSync]);

  // Tab switch / close is a sync point: flush pending edits for the OUTGOING
  // path. React destroys effects child-first, so the (keyed, already
  // unmounted) editor's state is still readable through getTextRef here.
  useEffect(() => {
    if (!path) return;
    const p = path;
    return () => {
      if (syncTimerRef.current != null) flushBufferSync(p);
      setSyncedText(null);
    };
  }, [path, flushBufferSync]);

  // Host replaced the buffer (reload / silent reload): drop the stale synced
  // text so the re-read below is what renders.
  const editingEpoch = editing?.state.epoch ?? -1;
  useEffect(() => {
    setSyncedText(null);
  }, [editingEpoch]);

  const state = editing?.state ?? null;
  // Delete-conflict: the path vanished in a refresh, OR a CAS
  // conflict came back without a live version (the file was deleted between
  // conflict and re-read). Rendered as a card below, replacing the editor.
  const deleteConflict =
    editing != null &&
    state !== null &&
    (state.notFound || (state.externalChange !== null && state.externalChange.currentVersion === null));

  // The card's appearance UNMOUNTS the editor, so it is a sync point: flush
  // the pending debounce so "keep my copy & save as new" acts on the text AS
  // TYPED, including sub-debounce edits (keep the buffer, never
  // truncate it to the last idle gap). The destroyed editor's state stays
  // readable through getTextRef (see its prop doc).
  useEffect(() => {
    if (!deleteConflict) return;
    const p = pathRef.current;
    if (p && syncTimerRef.current != null) flushBufferSync(p);
  }, [deleteConflict, flushBufferSync]);

  // Every resolution acts on the LATEST text: flush any pending sync first,
  // then hand the action to the host.
  const handleResolve = useCallback(
    (action: FileEditingResolveAction, mergedText?: string) => {
      const p = pathRef.current;
      if (!p) return;
      if (syncTimerRef.current != null) flushBufferSync(p);
      editingRef.current?.onResolve(p, action, mergedText);
    },
    [flushBufferSync],
  );

  // --- merge view (banner → Review changes → MergeView) -----------------------
  // Pane-level: while open it REPLACES the editor (closing returns). Only
  // reachable when the conflict carried the live content — the delete-conflict
  // card keeps its own surface.
  const [mergeOpen, setMergeOpen] = useState(false);
  const openMerge = useCallback(() => {
    const p = pathRef.current;
    if (!p) return;
    // Unconditional flush: the merge compares the text AS TYPED, including
    // sub-debounce edits (the editor unmounts while the merge view is open,
    // so the buffer is frozen at this point).
    flushBufferSync(p);
    setMergeOpen(true);
  }, [flushBufferSync]);
  // A tab switch or a resolved/vanished conflict closes the merge view.
  const conflictContent =
    state?.externalChange?.currentVersion != null ? state.externalChange.currentContent : undefined;
  useEffect(() => {
    setMergeOpen(false);
  }, [path]);
  useEffect(() => {
    if (conflictContent === undefined) setMergeOpen(false);
  }, [conflictContent]);

  // Host-side chrome (the tab strip's dirty-close dialog) flushes through
  // this ref before resolving a save, for the same latest-text guarantee.
  useEffect(() => {
    if (!flushPendingEditsRef) return;
    const ref = flushPendingEditsRef;
    ref.current = () => {
      const p = pathRef.current;
      if (p && syncTimerRef.current != null) flushBufferSync(p);
    };
    return () => {
      ref.current = null;
    };
  }, [flushPendingEditsRef, flushBufferSync]);

  // Decide, from extension + size ALONE, whether to fetch this file at all. This
  // is what prevents the freeze: an unsupported or oversized file is never read.
  const tooLarge = typeof sizeBytes === 'number' && sizeBytes > MAX_INLINE_BYTES;
  const shouldRead = !!path && canPreview(path) && !tooLarge;

  // Lazy "open externally" for the no-read placeholder: only fetch bytes when the
  // user actually clicks, and only up to the download bound.
  const canDownload = !!onOpenExternally && (typeof sizeBytes !== 'number' || sizeBytes <= MAX_DOWNLOAD_BYTES);
  const lazyOpenExternally = useCallback(() => {
    if (!path || !onOpenExternally) return;
    setDownloading(true);
    setDownloadError(null);
    read(path)
      .then((f) => onOpenExternally(path, bytesOf(f)))
      .catch((e: unknown) => setDownloadError(e instanceof Error ? e.message : String(e)))
      .finally(() => setDownloading(false));
  }, [path, read, onOpenExternally]);

  // Same open action for a file whose bytes are ALREADY loaded (the
  // read-but-unrenderable branch below) — no re-read, same error surface.
  const openLoadedExternally = useCallback(
    (fcLoaded: FileContent) => {
      if (!path || !onOpenExternally) return;
      setDownloadError(null);
      void Promise.resolve(onOpenExternally(path, bytesOf(fcLoaded))).catch((e: unknown) =>
        setDownloadError(e instanceof Error ? e.message : String(e)),
      );
    },
    [path, onOpenExternally],
  );

  useEffect(() => {
    let cancelled = false;
    if (!shouldRead) {
      setFc(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setFc(null);
    read(path)
      .then((result) => {
        if (!cancelled) {
          setFc(result);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // editingEpoch: the host bumps it after replacing the buffer, and its
    // cached `read` then resolves the fresh content without a network round
    // trip (per-tab cache).
  }, [shouldRead, path, read, editingEpoch]);

  const ext = path ? extOf(path) : '';

  // Build (and revoke) a blob URL for the image/pdf renderers only when the
  // active file is one of those formats.
  const needsBlob = ext === 'pdf' || ext in IMAGE_MIME;
  const blobUrl = useMemo(() => {
    if (!fc || !needsBlob) return null;
    const type = ext === 'pdf' ? 'application/pdf' : IMAGE_MIME[ext] ?? 'application/octet-stream';
    // .slice().buffer yields a tightly-sized ArrayBuffer (a valid BlobPart)
    // regardless of the Uint8Array view's offset/length.
    return URL.createObjectURL(new Blob([bytesOf(fc).slice().buffer], { type }));
  }, [fc, needsBlob, ext]);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!path) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <p className="text-body text-muted-foreground">Select a file to view it</p>
      </div>
    );
  }

  // Same card for both delete-conflict arms: keep-buffer-and-save-as-
  // new or close — never a lone discard.
  if (editing && state !== null && deleteConflict) {
    const base = path.split('/').pop() ?? path;
    return (
      <div
        className={cn('flex h-full flex-col items-center justify-center gap-2 p-6 text-center', className)}
        data-testid="file-not-found-card"
      >
        <p className="max-w-full truncate text-label font-medium" title={path}>{base}</p>
        <p className="text-body text-muted-foreground">
          This file no longer exists in the workspace.
          {state.dirty && ' Your unsaved edits are still in this tab.'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            size="sm"
            data-testid="save-as-new-file"
            disabled={state.saving || editing.readOnly}
            onClick={() => handleResolve('save-as-new')}
          >
            Keep my copy &amp; save as new
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="close-not-found-tab"
            onClick={() => handleResolve('discard-close')}
          >
            Close tab
          </Button>
        </div>
        {state.saveError && (
          <p className="text-caption text-destructive" role="alert">Save failed: {state.saveError}</p>
        )}
      </div>
    );
  }

  // Unsupported type or too large: we did NOT read the file. Offer a lazy,
  // bounded Open/Download instead of freezing on a huge inline read.
  if (!shouldRead) {
    const base = path.split('/').pop() ?? path;
    const reason = tooLarge
      ? `This file is ${formatBytes(sizeBytes)} — too large to preview here.`
      : 'This file type can’t be previewed.';
    return (
      <div className={cn('flex h-full flex-col items-center justify-center gap-2 p-6 text-center', className)}>
        <p className="max-w-full truncate text-label font-medium" title={path}>{base}</p>
        <p className="text-body text-muted-foreground">{reason}</p>
        {canDownload && (
          <>
            <button
              type="button"
              onClick={lazyOpenExternally}
              disabled={downloading}
              className="rounded-md bg-secondary px-3 py-1.5 text-body text-secondary-foreground transition-colors hover:bg-secondary/80 disabled:opacity-50"
            >
              {downloading ? 'Preparing…' : 'Open / Download'}
            </button>
            <p className="text-caption text-muted-foreground">
              Opens a copy — edits made in another app won’t be saved back to the workspace.
            </p>
          </>
        )}
        {!canDownload && onOpenExternally && (
          <p className="text-caption text-muted-foreground">Open it directly from your file system.</p>
        )}
        {downloadError && (
          <p className="text-caption text-destructive" role="alert">Could not open: {downloadError}</p>
        )}
      </div>
    );
  }
  if (loading) {
    return (
      <LoadingState label={`Loading ${path}…`} className={cn('h-full', className)} />
    );
  }
  if (error) {
    return (
      <div className={cn('flex h-full items-center justify-center p-4', className)}>
        <p className="text-body text-destructive" role="alert">
          Could not open {path}: {error}
        </p>
      </div>
    );
  }
  if (!fc) return null;

  // Editing wires into the TEXT-capable branches only, and only for utf8 reads
  // (a binary file has no text buffer to edit). Images/PDF/CSV/unsupported
  // never see it.
  const textEditable = editing != null && fc.encoding === 'utf8';
  const sourceEditing: ViewerSourceEditing | undefined = textEditable
    ? {
        readOnly: editing.readOnly,
        dirty: state?.dirty ?? false,
        onDocChanged: handleDocChanged,
        getTextRef,
        onSave: editing.readOnly ? undefined : handleEditorSave,
        editMode: editing.editMode,
        onEditModeChange: editing.onEditModeChange,
      }
    : undefined;
  // The buffer at the last sync point drives the viewers when editing;
  // otherwise the read result does. The CM6 editor reconciles `content` only
  // when it differs from its live doc, so echoing the synced text back never
  // loops.
  const bufferText = textEditable ? (syncedText ?? textOf(fc)) : null;
  // Keyed remount: per PATH so a tab switch resets undo history (Cmd+Z must
  // never restore another file's text), and per EPOCH so a host buffer
  // replacement (reload / silent reload) deterministically re-seeds the
  // editor with the fresh content.
  const editorKey = textEditable ? `${path}@${state?.epoch ?? 0}` : undefined;

  let body: React.ReactNode;
  if (ext === 'md' || ext === 'markdown') {
    body = (
      <MarkdownView
        key={editorKey}
        content={bufferText ?? textOf(fc)}
        filename={path}
        editing={sourceEditing}
      />
    );
  } else if (ext === 'csv') {
    body = <TableView content={textOf(fc)} delimiter="," />;
  } else if (ext === 'tsv') {
    body = <TableView content={textOf(fc)} delimiter={'\t'} />;
  } else if (ext in IMAGE_MIME && blobUrl) {
    body = <ImageView src={blobUrl} alt={path} />;
  } else if (ext === 'pdf' && blobUrl) {
    body = <PdfView src={blobUrl} />;
  } else if (HTML_EXTS.has(ext)) {
    body = (
      <HtmlView
        key={editorKey}
        content={bufferText ?? textOf(fc)}
        filename={path}
        editing={sourceEditing}
      />
    );
  } else if (CODE_EXTS.has(ext) || fc.encoding === 'utf8') {
    // Known code extensions, plus any other text (utf8) file, open in the
    // editor (see editorKey above for the remount semantics).
    body = (
      <TextEditorView
        key={editorKey}
        content={bufferText ?? textOf(fc)}
        filename={path}
        readOnly={textEditable ? editing.readOnly : true}
        onDocChanged={sourceEditing?.onDocChanged}
        getTextRef={sourceEditing?.getTextRef}
        onSave={sourceEditing?.onSave}
      />
    );
  } else {
    // Binary of an unknown type — never dump raw bytes.
    body = (
      <div className="flex flex-col gap-2">
        <UnsupportedView filename={path.split('/').pop() ?? path} onOpen={() => openLoadedExternally(fc)} />
        {downloadError && (
          <p className="text-caption text-destructive" role="alert">Could not open: {downloadError}</p>
        )}
      </div>
    );
  }

  // --- pane-level editing chrome (banners live HERE) ------------------------
  const externalChange = state?.externalChange ?? null;
  // Presentation of the conflict banner is DEFERRED while keystrokes continue
  // within the debounce window — the state itself is already
  // set (correctness); only the banner waits for the idle gap.
  const showConflictBanner =
    editing != null &&
    externalChange !== null &&
    externalChange.currentVersion !== null &&
    !syncPending &&
    !mergeOpen;
  const showSaveError = editing != null && state?.saveError != null;

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      {editing?.readOnly && editing.readOnlyNotice && (
        <div className="border-b border-border px-3 py-1 text-caption text-muted-foreground" data-testid="readonly-notice">
          {editing.readOnlyNotice}
        </div>
      )}
      {showConflictBanner && externalChange !== null && (
        <div
          className="flex flex-wrap items-center gap-2 bg-warning-subtle px-3 py-1.5 text-caption"
          data-testid="external-change-banner"
        >
          <span className="text-foreground">Changed since you loaded it — this file was modified outside this tab.</span>
          {externalChange.currentContent !== undefined && (
            <>
              {/* The merge view is only offered when the conflict carried the
                  live content (my-buffer-vs-current, always). */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-caption"
                data-testid="review-changes"
                onClick={openMerge}
              >
                Review changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-caption"
                data-testid="reload-external-change"
                onClick={() => handleResolve('reload')}
              >
                Reload
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            className="h-6 px-2 text-caption"
            disabled={state?.saving === true}
            data-testid="keep-mine-and-save"
            onClick={() => handleResolve('keep-mine')}
          >
            Keep mine &amp; save
          </Button>
        </div>
      )}
      {showSaveError && (
        <div
          className="flex flex-wrap items-center gap-2 bg-destructive/10 px-3 py-1.5 text-caption"
          data-testid="save-error-banner"
        >
          <span className="text-destructive" role="alert">Save failed: {state?.saveError}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 px-2 text-caption"
            disabled={state?.saving === true}
            data-testid="retry-save"
            onClick={() => handleResolve('retry-save')}
          >
            Retry save
          </Button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <React.Suspense
          fallback={
            <LoadingState label="Loading viewer…" className="h-full" />
          }
        >
          {/* While the merge view is open it replaces the editor pane-level;
              closing returns to the editor with the conflict state (and
              banner) intact. */}
          {mergeOpen && textEditable && conflictContent !== undefined ? (
            <MergeView
              filename={path}
              mine={bufferText ?? textOf(fc)}
              theirs={conflictContent}
              saving={state?.saving === true}
              onReload={() => {
                setMergeOpen(false);
                handleResolve('reload');
              }}
              onKeepMine={() => {
                setMergeOpen(false);
                handleResolve('keep-mine');
              }}
              onUseMerged={(text) => {
                setMergeOpen(false);
                handleResolve('merged', text);
              }}
              onClose={() => setMergeOpen(false)}
            />
          ) : (
            body
          )}
        </React.Suspense>
      </div>
    </div>
  );
}
